import { use, useEffect, useState } from "react";
import { Modal, Spin, message } from "antd";
import Spreadsheet from "react-spreadsheet";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import * as XLSX from "xlsx";
import mammoth from "mammoth";
import DocumentService from "../services/Document/DocumentService";

const DocumentPreviewModal = ({ isOpen, onClose, document }) => {
    const [viewDoc, setViewDoc] = useState([]);
    const [spreadsheetData, setSpreadsheetData] = useState(null);
    const [loading, setLoading] = useState(false);

    const getMimeType = (fileName) => {
        const ext = fileName?.split('.').pop().toLowerCase();
        const map = {
            pdf: 'application/pdf',
            doc: 'application/msword',
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            xls: 'application/vnd.ms-excel',
            xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            json: 'application/json',
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            png: 'image/png',
            gif: 'image/gif',
            bmp: 'image/bmp',
            webp: 'image/webp',
            svg: 'image/svg+xml',
            tiff: 'image/tiff',
            tif: 'image/tiff',
            txt: '/txt',
            csv: '/txt',
        };
        return map[ext] || 'application/octet-stream';
    };

    useEffect(() => {
        if (!isOpen || !document) return;
        const handlePreview = async (record) => {
            const fileName = record.fileName;
            const mimeType = getMimeType(fileName);
            const ext = fileName.split('.').pop().toLowerCase();
            const originalFileName = record.originalFileName;

            try {
                setLoading(true);
                const response = await DocumentService.handleDownload(record?.documentID || record?.id);
                if (response.status !== 200) {
                    throw new Error(`Failed to fetch file: ${response.statusText}`);
                }
                const blob = await response.data; // Define blob here

                if (ext === 'xls' || ext === 'xlsx') {
                    const arrayBuffer = await blob.arrayBuffer();
                    const workbook = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];

                    // Fill merged cells
                    if (worksheet['!merges']) {
                        worksheet['!merges'].forEach(m => {
                            const start = XLSX.utils.encode_cell({ r: m.s.r, c: m.s.c });
                            const baseValue = worksheet[start]?.v ?? '';
                            for (let R = m.s.r; R <= m.e.r; ++R)
                                for (let C = m.s.c; C <= m.e.c; ++C)
                                    if (!(R === m.s.r && C === m.s.c)) {
                                        const cell = XLSX.utils.encode_cell({ r: R, c: C });
                                        if (!worksheet[cell]) worksheet[cell] = { v: baseValue };
                                    }
                        });
                    }

                    const range = XLSX.utils.decode_range(worksheet['!ref']);
                    const rawData = XLSX.utils.sheet_to_json(worksheet, {
                        header: 1,
                        blankrows: true,
                        defval: '',
                        raw: true,
                        range,
                    });

                    const maxCols = range.e.c - range.s.c + 1;
                    const normalized = rawData.map(r =>
                        Array.from({ length: maxCols }, (_, i) => r[i] ?? '')
                    );

                    setSpreadsheetData(normalized);
                    setViewDoc([]);
                }
                else if (ext === 'docx') {
                    const arrayBuffer = await blob.arrayBuffer();
                    const { value: html } = await mammoth.convertToHtml({ arrayBuffer });

                    setSpreadsheetData(null);
                    setViewDoc([{ uri: null, fileType: 'docx', fileName, originalFileName, content: html }]);
                }
                else {
                    const blobUrl = URL.createObjectURL(new Blob([blob], { type: mimeType }));
                    setSpreadsheetData(null);
                    setViewDoc([{ uri: blobUrl, fileType: mimeType.split('/')[1], fileName: originalFileName, originalFileName }]);
                }
            } catch (err) {
                console.error('Failed to preview file:', err);
                message.error('Failed to load file preview. Please try again.');
                onClose();
            } finally {
                setLoading(false);
            }
        };
        handlePreview(document);
    }, [document, isOpen]);

    const handleModalClose = () => {
        onClose();
        setViewDoc([]);
        setSpreadsheetData(null) // Clear document when closing modal
    };

    useEffect(() => {
        return () => {
            viewDoc.forEach((doc) => {
                if (doc?.uri) URL.revokeObjectURL(doc.uri);
            });
        };
    }, [viewDoc]);


    return (
        <Modal
            title={viewDoc[0]?.originalFileName || 'Document Preview'}
            open={isOpen}
            onCancel={handleModalClose}
            footer={null}
            width={'90%'}
            styles={{
                body: { overflow: 'auto', height: '75vh' }
            }}
            destroyOnClose
        >
            {spreadsheetData ? (
                <div style={{ height: '100%', overflow: 'auto' }}>
                    <Spreadsheet
                        data={spreadsheetData.map(row =>
                            row.map(cell => ({ value: cell ?? '' }))
                        )}
                    />
                </div>
            ) : viewDoc.length > 0 ? (
                viewDoc[0].content ? (
                    <div
                        style={{ overflow: 'auto', padding: '1rem' }}
                        dangerouslySetInnerHTML={{ __html: viewDoc[0].content }}
                    />
                ) :
                    viewDoc.length > 0 && !viewDoc[0].content && (
                        <div style={{ height: '100%' }}>
                            <DocViewer
                                key={viewDoc[0].uri || viewDoc[0].fileName} // force remount
                                documents={viewDoc}
                                pluginRenderers={DocViewerRenderers}
                                style={{ height: '100%' }}
                                config={{
                                    header: {
                                        disableHeader: true,
                                        disableFileName: true,
                                        retainURLParams: true,
                                    },
                                }}
                            />
                        </div>
                    )
            )
                : loading ? (
                    <div className="flex items-center justify-center h-full">
                        <Spin size="large" tip="Loading preview..." />
                    </div>) : (
                    <div className="flex items-center justify-center h-full">
                        <span className="text-lg text-gray-500">No preview available</span>
                    </div>
                )}
        </Modal>
    );
}
export default DocumentPreviewModal;
